import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tag, X, Check, Loader2, ReceiptText, BadgePercent, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatUSD } from "@/lib/CatalogProvider";
import { validateCoupon } from "@/lib/adminApi";

/**
 * ReviewInvoice — premium invoice-style summary for the Package journey.
 *
 * Order (per client direction):
 *   1. Line items (package + add-ons)  → the "package details"
 *   2. Subtotal                        → the "final price"
 *   3. Coupon field (apply a % code)   → "option to add coupons"
 *   4. Discount row (if applied)       → "price will drop"
 *   5. BIG final total                 → "final number in big"
 *
 * Coupons are validated against the backend (`POST /api/coupon/validate`),
 * and the % applies to the whole estimated total (base + add-ons).
 */
export const ReviewInvoice = ({
    selectedPackage,
    selectedAddons = [],
    basePrice = 0,
    estimatedTotal = 0,
    couponCode = "",
    couponPercent = 0,
    onApplyCoupon,
    onRemoveCoupon,
}) => {
    const [input, setInput] = useState("");
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState(null);

    const applied = !!couponCode && Number(couponPercent) > 0;
    const subtotal = Number(estimatedTotal || 0);
    const discount = applied ? Math.round(subtotal * Number(couponPercent)) / 100 : 0;
    const finalTotal = Math.round((subtotal - discount) * 100) / 100;

    const apply = async () => {
        const code = (input || "").trim();
        if (!code) {
            setError("Enter a coupon code.");
            return;
        }
        setChecking(true);
        setError(null);
        try {
            const res = await validateCoupon(code);
            if (res?.valid) {
                onApplyCoupon?.(res.code, res.percent);
                setInput("");
                toast.success(res.message || "Coupon applied", {
                    description: `${res.code} · ${Math.round(res.percent)}% off your total.`,
                });
            } else {
                setError(res?.message || "That code isn't valid.");
                toast.error("Coupon not applied", { description: res?.message || "Please check the code." });
            }
        } catch (_e) {
            setError("Couldn't check that code. Please try again.");
            toast.error("Coupon check failed", { description: "Please try again in a moment." });
        } finally {
            setChecking(false);
        }
    };

    const remove = () => {
        onRemoveCoupon?.();
        setError(null);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            apply();
        }
    };

    return (
        <div
            className="pg-invoice rounded-3xl border p-5 sm:p-7"
            style={{
                borderColor: "color-mix(in srgb, var(--pg-accent) 32%, var(--pg-border))",
                background: "linear-gradient(150deg, color-mix(in srgb, var(--pg-accent) 7%, var(--pg-surface)) 0%, var(--pg-surface) 100%)",
                boxShadow: "0 26px 66px -34px color-mix(in srgb, var(--pg-accent) 30%, rgba(15,45,55,0.4))",
            }}
            data-testid="review-invoice"
        >
            <div className="flex items-center gap-2">
                <span
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full"
                    style={{ background: "color-mix(in srgb, var(--pg-accent) 14%, var(--pg-surface))", color: "var(--pg-accent)" }}
                >
                    <ReceiptText className="h-4 w-4" strokeWidth={1.8} />
                </span>
                <span className="text-[12px] font-semibold tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>
                    Invoice Summary
                </span>
            </div>

            {/* Line items */}
            <ul className="mt-5 space-y-3" data-testid="invoice-line-items">
                <li className="flex items-baseline justify-between gap-4">
                    <div className="min-w-0">
                        <div className="font-display text-[15px] font-semibold" style={{ color: "var(--pg-fg)" }}>
                            {selectedPackage?.name || "Selected Package"}
                        </div>
                        <div className="text-[12px]" style={{ color: "var(--pg-fg-muted)" }}>
                            {selectedPackage?.category || "Package"} · base
                        </div>
                    </div>
                    <span className="pg-price-num text-[15px]" style={{ color: "var(--pg-fg)" }}>
                        {formatUSD(basePrice)}
                    </span>
                </li>

                {selectedAddons.map((a) => (
                    <li key={a.id} className="flex items-baseline justify-between gap-4">
                        <div className="min-w-0 text-[13px]" style={{ color: "var(--pg-fg)" }}>
                            <span className="opacity-70">+</span> {a.name}
                        </div>
                        <span className="pg-price-num text-[13px]" style={{ color: "var(--pg-fg-muted)" }}>
                            {formatUSD(a.price)}
                        </span>
                    </li>
                ))}
            </ul>

            <Divider />

            {/* Subtotal */}
            <div className="flex items-baseline justify-between">
                <span className="text-[13px]" style={{ color: "var(--pg-fg-muted)" }}>Subtotal</span>
                <span className="pg-price-num text-[16px]" style={{ color: "var(--pg-fg)" }} data-testid="invoice-subtotal">
                    {formatUSD(subtotal)}
                </span>
            </div>

            {/* Coupon */}
            <div className="mt-5">
                {!applied ? (
                    <div>
                        <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "var(--pg-fg-muted)" }}>
                            <Tag className="h-3.5 w-3.5" /> Have a coupon code?
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => { setInput(e.target.value.toUpperCase()); setError(null); }}
                                onKeyDown={onKeyDown}
                                placeholder="e.g. PGFX20"
                                aria-label="Coupon code"
                                data-testid="coupon-input"
                                className={`pg-input flex-1${error ? " pg-input--error" : ""}`}
                                style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
                            />
                            <button
                                type="button"
                                onClick={apply}
                                disabled={checking}
                                data-testid="coupon-apply"
                                className="pg-btn-primary shrink-0 justify-center disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgePercent className="h-4 w-4" strokeWidth={1.8} />}
                                {checking ? "Checking…" : "Apply"}
                            </button>
                        </div>
                        <AnimatePresence>
                            {error && (
                                <motion.p
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="mt-2 text-[12px]"
                                    style={{ color: "#E5736F" }}
                                    data-testid="coupon-error"
                                >
                                    {error}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between gap-3 rounded-2xl border px-3.5 py-3"
                        style={{
                            borderColor: "color-mix(in srgb, var(--pg-accent) 45%, var(--pg-border))",
                            background: "color-mix(in srgb, var(--pg-accent) 10%, var(--pg-surface))",
                        }}
                        data-testid="coupon-applied"
                    >
                        <div className="flex items-center gap-2">
                            <span
                                className="inline-flex h-6 w-6 items-center justify-center rounded-full"
                                style={{ background: "var(--pg-accent)", color: "var(--pg-accent-fg)" }}
                            >
                                <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                            </span>
                            <div className="leading-tight">
                                <div className="font-display text-[14px] font-bold" style={{ color: "var(--pg-fg)" }} data-testid="coupon-applied-code">
                                    {couponCode}
                                </div>
                                <div className="text-[12px]" style={{ color: "var(--pg-fg-muted)" }}>
                                    {Math.round(couponPercent)}% off applied
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={remove}
                            data-testid="coupon-remove"
                            aria-label="Remove coupon"
                            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors hover:brightness-105"
                            style={{ borderColor: "var(--pg-border-strong)", color: "var(--pg-fg-muted)", background: "var(--pg-surface)" }}
                        >
                            <X className="h-3 w-3" /> Remove
                        </button>
                    </motion.div>
                )}
            </div>

            {/* Discount row */}
            <AnimatePresence>
                {applied && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 flex items-baseline justify-between overflow-hidden"
                        data-testid="invoice-discount"
                    >
                        <span className="inline-flex items-center gap-1.5 text-[13px]" style={{ color: "var(--pg-accent)" }}>
                            <Sparkles className="h-3.5 w-3.5" /> Discount ({Math.round(couponPercent)}%)
                        </span>
                        <span className="pg-price-num text-[15px]" style={{ color: "var(--pg-accent)" }}>
                            −{formatUSD(discount)}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            <Divider />

            {/* Big final total */}
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="text-[12px] font-semibold" style={{ color: "var(--pg-fg-muted)" }}>Total due</div>
                    {applied && (
                        <div className="mt-0.5 text-[12px]" style={{ color: "var(--pg-fg-muted)" }}>
                            <span style={{ textDecoration: "line-through", opacity: 0.7 }}>{formatUSD(subtotal)}</span>
                            <span className="ml-1.5" style={{ color: "var(--pg-accent)" }}>you save {formatUSD(discount)}</span>
                        </div>
                    )}
                </div>
                <motion.span
                    key={finalTotal}
                    initial={{ scale: 0.94, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.34, ease: [0.16, 1, 0.3, 1] }}
                    className="pg-price-num pg-price-gold text-4xl sm:text-5xl"
                    data-testid="invoice-final-total"
                >
                    {formatUSD(finalTotal)}
                </motion.span>
            </div>
        </div>
    );
};

const Divider = () => (
    <div
        className="my-5 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--pg-border-strong), transparent)" }}
    />
);

export default ReviewInvoice;
